/**
 * useOpenTabsStore.ts
 *
 * Lightweight external store for Test Case editor tabs.
 * Uses `useSyncExternalStore` (no extra deps) + `sessionStorage` persistence.
 *
 * Supports Context Multiplexing: Tabs are isolated by projectId, workspaceId, and versionId.
 */

const STORAGE_KEY = "aq:openTestCaseTabs";

/* ── Types ────────────────────────────────────────────── */

export interface OpenTab {
  id: number;
  name: string;
  scriptType: string; // "automation" | "manual"
  status?: string; // "READY" | "DRAFT" | "IN_REVIEW" | "OBSOLETE" | ...
  folderId?: number; // Used for fast folder restoration
  testCaseRaw?: any; // The full test case object used for tooltips
}

export interface TabStoreState {
  tabs: OpenTab[];
  activeTabId: number | null;
}

export type GlobalTabState = Record<string, TabStoreState>;

/* ── Context & Persistence ────────────────────────────── */

function getContextKey(
  projectId?: number,
  workspaceId?: number,
  versionId?: number,
): string {
  if (!projectId || !workspaceId || !versionId) return "default";
  return `${projectId}_${workspaceId}_${versionId}`;
}

let currentContextKey = "default";

function loadFromSession(): GlobalTabState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);

    // Legacy migration: if it's an array or has 'tabs', it's the old flat state
    if (Array.isArray(parsed) || Array.isArray(parsed?.tabs)) {
      return {
        default: {
          tabs: Array.isArray(parsed)
            ? parsed
            : Array.isArray(parsed?.tabs)
              ? parsed.tabs
              : [],
          activeTabId:
            typeof parsed?.activeTabId === "number" ? parsed.activeTabId : null,
        },
      };
    }

    return parsed as GlobalTabState;
  } catch {
    return {};
  }
}

function saveToSession(stateToSave: GlobalTabState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  } catch {
    /* quota exceeded — degrade gracefully */
  }
}

let globalState: GlobalTabState = loadFromSession();

/* ── Store Core ───────────────────────────────────────── */

const EMPTY_STATE: TabStoreState = { tabs: [], activeTabId: null };

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

function getCurrentState(): TabStoreState {
  return globalState[currentContextKey] || EMPTY_STATE;
}

function getSnapshot(): TabStoreState {
  return getCurrentState();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ── Actions ──────────────────────────────────────────── */

/**
 * Update the current context (e.g., when switching Workspace/Project).
 * Designed to be called synchronously during render to avoid flashes.
 */
function setContext(
  projectId?: number,
  workspaceId?: number,
  versionId?: number,
): void {
  const newKey = getContextKey(projectId, workspaceId, versionId);
  if (currentContextKey !== newKey) {
    currentContextKey = newKey;
    // No emit() here - called during render so React will naturally read the new snapshot
  }
}

/**
 * Open (or re-activate) a tab for a test case.
 */
function openTab(tab: OpenTab): void {
  const currentState = getCurrentState();
  const exists = currentState.tabs.find((t) => t.id === tab.id);

  let newState: TabStoreState;
  if (exists) {
    const updatedTabs = currentState.tabs.map((t) =>
      t.id === tab.id ? { ...t, ...tab } : t,
    );
    newState = { tabs: updatedTabs, activeTabId: tab.id };
  } else {
    newState = {
      tabs: [...currentState.tabs, tab],
      activeTabId: tab.id,
    };
  }

  globalState = { ...globalState, [currentContextKey]: newState };
  saveToSession(globalState);
  emit();
}

/**
 * Close a tab. If it was the active tab, activate the nearest neighbor.
 */
function closeTab(id: number): void {
  const currentState = getCurrentState();
  const idx = currentState.tabs.findIndex((t) => t.id === id);
  if (idx === -1) return;

  const newTabs = currentState.tabs.filter((t) => t.id !== id);
  let newActive = currentState.activeTabId;

  if (currentState.activeTabId === id) {
    if (newTabs.length === 0) {
      newActive = null;
    } else {
      const nextIdx = Math.min(idx, newTabs.length - 1);
      newActive = newTabs[nextIdx].id;
    }
  }

  const newState = { tabs: newTabs, activeTabId: newActive };
  globalState = { ...globalState, [currentContextKey]: newState };
  saveToSession(globalState);
  emit();
}

/**
 * Switch active tab without adding/removing.
 */
function setActiveTab(id: number | null): void {
  const currentState = getCurrentState();
  if (currentState.activeTabId === id) return;

  const newState = { ...currentState, activeTabId: id };
  globalState = { ...globalState, [currentContextKey]: newState };
  saveToSession(globalState);
  emit();
}

/**
 * Patch a tab's metadata (e.g. update name after API fetch).
 */
function updateTab(id: number, patch: Partial<OpenTab>): void {
  const currentState = getCurrentState();
  const idx = currentState.tabs.findIndex((t) => t.id === id);
  if (idx === -1) return;

  const updatedTabs = [...currentState.tabs];
  updatedTabs[idx] = { ...updatedTabs[idx], ...patch };

  const newState = { ...currentState, tabs: updatedTabs };
  globalState = { ...globalState, [currentContextKey]: newState };
  saveToSession(globalState);
  emit();
}

/* ── Public API ───────────────────────────────────────── */

export const tabStore = {
  subscribe,
  getSnapshot,
  setContext,
  openTab,
  closeTab,
  setActiveTab,
  updateTab,
};
